import { Grid as GridBase, Paper, Typography } from "@mui/material";
import React, { useMemo } from "react";
import RpImageTextInlineBase from "@/components/RpImageTextInline";
import { CurrencyFormateText as CurrencyFormateTextBase } from "@/components/CurrencyFormat";
import { getFormatDate } from "@/helpers/utils";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";

type CreativeId = number | string;

type ConfirmCreative = {
  id?: CreativeId;
  is_selected?: boolean;
  creative_name?: string;
  [key: string]: unknown;
};

type ConfirmLocationData = {
  location_name?: string;
  start_date?: string;
  end_date?: string;
  extend_start_date?: string;
  extend_end_date?: string;
  extend_budget?: number | string;
  can_update_creatives?: boolean;
  extend_creatives?: CreativeId[];
  creatives?: ConfirmCreative[];
};

type ConfirmLocationProps = {
  data?: ConfirmLocationData;
  campaign_creatives?: ConfirmCreative[];
};

const RpImageTextInline = RpImageTextInlineBase as React.ComponentType<
  Record<string, unknown>
>;

const CurrencyFormateText = CurrencyFormateTextBase as React.ComponentType<
  Record<string, unknown>
>;

const Grid = GridBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

function ConfirmLocation({
  data,
  campaign_creatives = [],
}: ConfirmLocationProps) {
  const selected_creatives = useMemo(() => {
    if (data?.can_update_creatives && data?.extend_creatives?.length) {
      return campaign_creatives
        .filter((c) => data?.extend_creatives?.includes(c.id))
        ?.map((c) => getCreativePackageLabel(c))
        .join(", ");
    }

    return data?.creatives?.map((c) => c.creative_name).join(", ");
  }, [campaign_creatives, data]);
  return (
    <Paper className="mb-2 p-3" elevation={2}>
      <Grid container spacing={3} className="p-1">
        <Grid item size={{ xs: 12, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="location_mark"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Location</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  component="p"
                  variant="body2"
                >
                  {data?.location_name}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="month_calender"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Current Start Date</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {getFormatDate(data?.start_date)}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="month_calender"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Current End Date</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {getFormatDate(data?.end_date)}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="wallet"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Current Offer</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {campaign_creatives
                    ?.filter((c) => c.is_selected === true)
                    ?.map((c) => getCreativePackageLabel(c))
                    .join(", ")}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="month_calender"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">New Start Date</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {getFormatDate(data?.extend_start_date)}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="month_calender"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">New End Date</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {getFormatDate(data?.extend_end_date)}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="wallet"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Selected Offer</Typography>
                <Typography
                  className="title-val fw-sb mt-2"
                  variant="body2"
                  component="p"
                >
                  {selected_creatives}
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="wallet"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">Budget</Typography>
                <Typography
                  className="fw-sb mt-2"
                  variant="caption"
                  component="p"
                  color="text.primary"
                >
                  <CurrencyFormateText
                    value={data?.extend_budget}
                    suffix=" USD"
                  />
                </Typography>
              </>
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ConfirmLocation;
